import React from "react";
import { connect } from "react-redux";
import { any, func } from "prop-types";
import moment from "moment/moment";
import history from "../../history";
import FormCreatorV2 from "../../common/FormCreatorV2";
import { submitDnataExtraForm } from "../../redux/actions/index";
import {
  formCreatorConvertToValues,
  getField,
  useForm,
} from "../../common/FormCreator/helpers";
import { buildAddress } from "../../helpers/form";
import HorizontalLine from "../../common/HorizontalLine/index";

const HealthQuestionnaire = ({
  appData: {
    dnataOnboarding,
    firstName,
    lastName,
    address,
    dateOfBirth,
    mobileTelephone,
  },
  submitForm,
}) => {
  const typeCode = "YDN8";
  const fullName = `${firstName} ${lastName}`;
  const submitted = dnataOnboarding && dnataOnboarding[typeCode];

  const initialData = {
    lastName,
    firstName,
    homeAddress: buildAddress(address, false),
    homePostcode: address.postcode,
    homeTelephone: address.homeTelephone,
    mobileTelephone,
    signature: fullName,
    date: moment().format("DD/MM/YYYY"),
    dateOfBirth,
  };

  const getVal = getField(initialData);

  const initialState = {
    blurb1: {
      htmlObj: (
        <div className="font-weight-bold text-uppercase">
          <p>Strictly private & confidential</p>
          <p>Initial health assessment at induction</p>
        </div>
      ),
    },
    lastName: {
      title: "Surname",
      value: getVal("lastName"),
      mandatory: true,
    },
    firstName: {
      title: "Forenames",
      value: getVal("firstName"),
      mandatory: true,
    },
    dateOfBirth: {
      title: "Date of Birth",
      type: "date",
      value: getVal("dateOfBirth"),
      mandatory: true,
    },
    homeAddress: {
      title: "Home Address",
      value: getVal("homeAddress"),
      mandatory: true,
    },
    homeTelephone: {
      title: "Home telephone number",
      value: getVal("homeTelephone"),
    },
    mobileTelephone: {
      title: "Mobile",
      value: getVal("mobileTelephone"),
      mandatory: true,
    },
    homePostcode: {
      title: "Postcode",
      value: getVal("homePostcode"),
      mandatory: true,
    },
    blurb2: {
      htmlObj: (
        <div className="font-weight-bold text-uppercase">
          <p>Please read the questions carefully.</p>
        </div>
      ),
    },
    gpName: {
      title: "Name of GP",
      value: getVal("gpName"),
      mandatory: true,
    },
    gpAddress: {
      title: "Address",
      value: getVal("gpAddress"),
      mandatory: true,
    },
    gpCountry: {
      title: "Country",
      value: getVal("gpCountry"),
      mandatory: true,
    },
    gpLocation: {
      title: "Location",
      value: getVal("gpLocation"),
      mandatory: true,
    },
    gpTelephone: {
      title: "Telephone Number",
      value: getVal("gpTelephone"),
      mandatory: true,
    },
    blurb3: {
      htmlObj: <HorizontalLine text="Section 1" />,
    },
    illness: {
      title:
        "1. In the past 12 months have you had any illness or accidents requiring hospital admission?",
      value: getVal("illness"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    illnessDependOn: {
      title: "If yes, please give details below.",
      value: getVal("illnessDependOn"),
      mandatory: true,
      dependOn: [{ fieldKey: "illness" }],
    },
    surgical: {
      title:
        "2. Have you undergone any surgical procedure in the last 2 years?",
      value: getVal("surgical"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    surgicalDependOn: {
      title: "If yes, please give details below.",
      value: getVal("surgicalDependOn"),
      mandatory: true,
      dependOn: [{ fieldKey: "surgical" }],
    },
    medication: {
      title: "3. Are you taking regular medication?",
      value: getVal("medication"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    medicationDependOn: {
      title: "If yes, please give details below.",
      value: getVal("medicationDependOn"),
      mandatory: true,
      dependOn: [{ fieldKey: "medication" }],
    },
    fatigue: {
      title:
        "4. Have you suffered from any problems associated with fatigue, sleep disturbance, or eating disorders?",
      value: getVal("fatigue"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    fatigueDependOn: {
      title: "If yes, please give details below.",
      value: getVal("fatigueDependOn"),
      mandatory: true,
      dependOn: [{ fieldKey: "fatigue" }],
    },
    limbs: {
      title: "5. Have you had any working problems with upper limbs?",
      value: getVal("limbs"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    limbsDependOn: {
      title: "If yes, please give details below.",
      value: getVal("limbsDependOn"),
      mandatory: true,
      dependOn: [{ fieldKey: "limbs" }],
    },
    nightWork: {
      title: "6. Have you ever experienced problems with night work?",
      value: getVal("nightWork"),
      type: "radio",
      radioOptions: ["Yes", "No", "N/A"],
      mandatory: true,
    },
    nightWorkDependOn: {
      title: "If yes, please give details below.",
      value: getVal("nightWorkDependOn"),
      mandatory: true,
      dependOn: [{ fieldKey: "nightWork", allowedValues: ["Yes"] }],
    },
    voluntary: {
      title:
        "7. In addition to your job with Dnata catering UK Ltd do you carry out any work either paid or voluntary?",
      value: getVal("voluntary"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    voluntaryDependOn: {
      title: "If yes, please give details below.",
      value: getVal("voluntaryDependOn"),
      mandatory: true,
      dependOn: [{ fieldKey: "voluntary" }],
    },
    blurb4: {
      htmlObj: (
        <div>
          8. Have you suffered / or are you suffering from the following?
        </div>
      ),
    },
    heartDisease: {
      title: "Heart Disease",
      value: getVal("heartDisease"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    bloodPressure: {
      title: "High Blood Pressure",
      value: getVal("bloodPressure"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    diabetes: {
      title: "Diabetes",
      value: getVal("diabetes"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    dizzy: {
      title: "Dizzy Spells",
      value: getVal("dizzy"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    blackouts: {
      title: "Fits / Blackouts",
      value: getVal("blackouts"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    stress: {
      title: "Stress/Depression/Anxiety",
      value: getVal("stress"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    backPain: {
      title: "Back Pain",
      value: getVal("backPain"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    neckPain: {
      title: "Neck Pain",
      value: getVal("neckPain"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    indigestion: {
      title: "Indigestion",
      value: getVal("indigestion"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    stomach: {
      title: "Stomach/bowel problems",
      value: getVal("stomach"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    respiratory: {
      title: "Respiratory",
      value: getVal("respiratory"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    sufferedDependOn: {
      title:
        "If you have answered 'Yes' to any of the above questions please give full details below.",
      value: getVal("sufferedDependOn"),
      type: "textarea",
      rows: 5,
      mandatory: true,
      dependOnAny: [
        { fieldKey: "heartDisease" },
        { fieldKey: "bloodPressure" },
        { fieldKey: "diabetes" },
        { fieldKey: "dizzy" },
        { fieldKey: "blackouts" },
        { fieldKey: "stress" },
        { fieldKey: "backPain" },
        { fieldKey: "neckPain" },
        { fieldKey: "indigestion" },
        { fieldKey: "stomach" },
        { fieldKey: "respiratory" },
      ],
    },
    blind: {
      title: "9. Are you colour blind?",
      value: getVal("blind"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    hearing: {
      title:
        "10. To the best of your knowledge do you suffer from any loss of hearing difficulties including tinnitus?",
      value: getVal("hearing"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    hearingDependOn: {
      title: "If yes, please give details below.",
      value: getVal("hearingDependOn"),
      mandatory: true,
      dependOn: [{ fieldKey: "hearing" }],
    },
    accident: {
      title:
        "11. Have you, in the last 6 years suffered a work place accident which resulted in the submission of a report under the RIDDOR Regulations?",
      value: getVal("accident"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    accidentDependOn: {
      title: "If yes, please give details below.",
      value: getVal("accidentDependOn"),
      mandatory: true,
      dependOn: [{ fieldKey: "accident" }],
    },
    blurb5: {
      htmlObj: (
        <div>
          12. Have you had food poisoning or food born illness in the past 60
          days which could be transmitted through food handling or via contact
          with people/equipment?
        </div>
      ),
    },
    hepatitis: {
      title: "Hepatitis A Virus",
      value: getVal("hepatitis"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    hepatitisDependOn: {
      title: "If yes, please give details below.",
      value: getVal("hepatitisDependOn"),
      mandatory: true,
      type: "date",
      dependOn: [{ fieldKey: "hepatitis" }],
    },
    norovirus: {
      title: "Norovirus (Including Norwalk, 'winter vomiting bug')",
      value: getVal("norovirus"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    norovirusDependOn: {
      title: "If yes, please give details below.",
      value: getVal("norovirusDependOn"),
      mandatory: true,
      type: "date",
      dependOn: [{ fieldKey: "norovirus" }],
    },
    salmonella: {
      title: "Any type of Salmonella",
      value: getVal("salmonella"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    salmonellaDependOn: {
      title: "If yes, please give details below.",
      value: getVal("salmonellaDependOn"),
      mandatory: true,
      type: "date",
      dependOn: [{ fieldKey: "salmonella" }],
    },
    shigella: {
      title: "Any type of Shigella",
      value: getVal("shigella"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    shigellaDependOn: {
      title: "If yes, please give details below.",
      value: getVal("shigellaDependOn"),
      mandatory: true,
      type: "date",
      dependOn: [{ fieldKey: "shigella" }],
    },
    aureus: {
      title: "Staphylococcus aureus",
      value: getVal("aureus"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    aureusDependOn: {
      title: "If yes, please give details below.",
      value: getVal("aureusDependOn"),
      mandatory: true,
      type: "date",
      dependOn: [{ fieldKey: "aureus" }],
    },
    ecoli: {
      title: "Any illness due to Escherichia coli (EColi)",
      value: getVal("ecoli"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    ecoliDependOn: {
      title: "If yes, please give details below.",
      value: getVal("ecoliDependOn"),
      mandatory: true,
      type: "date",
      dependOn: [{ fieldKey: "ecoli" }],
    },
    blurb6: {
      htmlObj: (
        <div>
          Has anyone in your household suffered from the following in the last
          60 days:
        </div>
      ),
    },
    ecoliHousehold: {
      title: "Any illness due to Escherichia coli (EColi)",
      value: getVal("ecoliHousehold"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    ecoliHouseholdDependOn: {
      title: "If yes, please give details below.",
      value: getVal("ecoliHouseholdDependOn"),
      mandatory: true,
      type: "date",
      dependOn: [{ fieldKey: "ecoliHousehold" }],
    },
    typhoid: {
      title: "Typhoid/Paratyphoid",
      value: getVal("typhoid"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    typhoidDependOn: {
      title: "If yes, please give details below.",
      value: getVal("typhoidDependOn"),
      mandatory: true,
      type: "date",
      dependOn: [{ fieldKey: "typhoid" }],
    },
    blurb7: {
      htmlObj: (
        <div>
          13. Are you currently experiencing any of the following symptoms?
        </div>
      ),
    },
    diarrhoea: {
      title: "Diarrhoea",
      value: getVal("diarrhoea"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    fever: {
      title: "Fever",
      value: getVal("fever"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    vomiting: {
      title: "Vomiting",
      value: getVal("vomiting"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    jaundice: {
      title: "Jaundice",
      value: getVal("jaundice"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    soreThroat: {
      title: "Sore throat (Discharge from throat, nose or ear)",
      value: getVal("soreThroat"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    pus: {
      title:
        "Any lesions containing pus (e.g. boils and infected wounds) on forearm, hand, wrist or any exposed body part?",
      value: getVal("pus"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    musculoskeletal: {
      title:
        "16. Do you suffer from any musculoskeletal impairment/difficulties?",
      value: getVal("musculoskeletal"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    musculoskeletalDependOn: {
      title: "If yes, please give details below.",
      value: getVal("musculoskeletalDependOn"),
      mandatory: true,
      dependOn: [{ fieldKey: "musculoskeletal" }],
    },
    disability: {
      title:
        "17. Have you got a disability that you would like us to know about?",
      value: getVal("disability"),
      type: "checkbox",
      checkboxText1: "Yes",
      checkboxText2: "No",
      mandatory: true,
    },
    disabilityDependOn: {
      title:
        "If yes, please give details below on what adjustments we can make to assist you.",
      value: getVal("disabilityDependOn"),
      mandatory: true,
      dependOn: [{ fieldKey: "disability" }],
    },
    blurb8: {
      htmlObj: <HorizontalLine text="Declaration" />,
    },
    blurb9: {
      htmlObj: (
        <div>
          <p>
            I declare that the answers contained in the Questionnaire are to the
            best of my knowledge true. I have read and understood my obligations
            to inform Dnata catering UK Ltd of any of the above health
            conditions, and will inform Dnata catering UK Ltd of future
            symptoms.
          </p>
          <p>
            I will agree to further health interviews/examinations if this is
            required. <br />
            *N.B. No approach will be made to your doctor without your signed
            consent.
          </p>
        </div>
      ),
    },
    signature: {
      key: "signature",
      title: "Signature",
      value: getVal("signature"),
      disabled: true,
    },
    date: {
      key: "date",
      title: "Date",
      value: getVal("date"),
      disabled: true,
    },
  };
  const [fields, setField] = useForm(initialState);

  const redirectBack = () => {
    history.push("/profile/edit/dnata-onboarding");
  };

  const handleSubmit = () => {
    const fieldValuesPayload = formCreatorConvertToValues(fields);
    submitForm(typeCode, {
      ...fieldValuesPayload,
    });
    redirectBack();
  };

  return (
    <>
      {submitted ? (
        redirectBack()
      ) : (
        <FormCreatorV2
          formTitle={"Health Questionnaire"}
          fields={fields}
          setField={setField}
          handleCancel={() => redirectBack()}
          onboardingBranch={"dnata"}
          useSubmitText={true}
          handleSubmit={handleSubmit}
        />
      )}
    </>
  );
};

HealthQuestionnaire.propTypes = {
  appData: any,
  submitForm: func,
};

const mapDispatchToProps = {
  submitForm: submitDnataExtraForm,
};
const mapStateToProps = (state) => ({
  appData: state.core.app,
});

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(HealthQuestionnaire);
